"use client"

import { useDispatch, useSelector } from "react-redux"
import Image from "../images/Image"
import InputFieldForPhoneNumber from "../inputfields/InputFieldForPhoneNumber"
import {
  addPhoneNumber,
  removePhoneNumber,
  setErrorById,
  setIsVerifiedById,
  setVerifiedSecondaryPhoneNumbers,
  updatePhoneNumber,
} from "../../store/slices/phoneNumbersSlice"
import type { RootState } from "../../store"
import CheckBoxWithLabel from "../checkbox/CheckBoxWithLabel"
import { showToast } from "../toastMessage/toastSlice"
import { useState } from "react"
import CustomModal from "../CustomModal"
import { setUpdateMDStates, setVerifiedPhoneNumber, setSecondaryPhoneNumbers } from "../../pages/UserManagement/MasterDistributorManagement/addMDSlice"
import useTranslation from "../CustomHooks/useTranslation"
import { inputFieldContent } from "../inputfields/InputFieldContent"

interface ModalData {
  phone: {
    id: string;
    value: string;
    countryCode: string;
  };
  index: number;
}

interface MultiPhoneNumbersProps {
  handleVerify?: (id: string, resend: boolean, type?: string, idNumber?: string) => void;
  handleSubmitOTP: (key?: string) => void;
  disabled?: boolean;
  isOpenOtpModel?: boolean;
  setIsOpenOtpModel?: React.Dispatch<React.SetStateAction<boolean>>;
  edit: boolean;
  editLoading: boolean;
  mapping:boolean;
  maxLength?: number;
  onSecondaryPhoneNumbersChange?: (phones: { country_code: string; phone_number: string }[]) => void;
}

export default function MultiPhoneNumbers({
  handleVerify,
  handleSubmitOTP,
  disabled,
  setIsOpenOtpModel,
  isOpenOtpModel,
  edit,
  editLoading,
  mapping,
  maxLength,
  onSecondaryPhoneNumbersChange,
}: MultiPhoneNumbersProps) {
  const dispatch = useDispatch()
  const { phoneNumbers } = useSelector((state: RootState) => state.phoneNumbers)
  const { translate} = useTranslation(inputFieldContent);
  const { phone_number, country_code } = useSelector((state: RootState) => state.addMD.addAdminDetails)
  const [modalData, setModalData] = useState<ModalData | null>(null)

  const handleAddPhoneNumber = () => {
    dispatch(addPhoneNumber())
  }

  const handleRemovePhoneNumber = (idToRemove: string) => {
    dispatch(removePhoneNumber(idToRemove))
  }

  const handleChange = (id: string, field: string, value: string) => {
    dispatch(setErrorById({ id, error: '' }))
    // dispatch(setIsVerifiedById({ id, isVerified: '' }))
    dispatch(updatePhoneNumber({ id, field, value }))
    
    // After updating, map phoneNumbers to array of { country_code, phone_number }
    // Assume phoneNumbers is up-to-date enough for this context
    const secondaryPhoneNumbers = phoneNumbers.map((phone: any) => {
      let countryCode = phone.countryCode ? (phone.countryCode.startsWith('+') ? phone.countryCode : `+${phone.countryCode}`) : '';
      let phoneNumber = phone.id === id ? value : phone.value;
      // Remove country code (with or without plus) from the start of phone.value
      if (countryCode && phoneNumber.startsWith(countryCode)) {
        phoneNumber = phoneNumber.slice(countryCode.length);
      } else if (countryCode && countryCode.startsWith('+') && phoneNumber.startsWith(countryCode.replace('+', ''))) {
        phoneNumber = phoneNumber.slice(countryCode.length - 1);
      }
      return {
        country_code: countryCode,
        phone_number: phoneNumber
      };
    });
    if (onSecondaryPhoneNumbersChange) {
      onSecondaryPhoneNumbersChange(secondaryPhoneNumbers);
    } else {
      dispatch(setSecondaryPhoneNumbers(secondaryPhoneNumbers));
    }
    // dispatch(updatePhoneNumber({ id, field: 'isVerified', value: '' }))
  }

  const handleCheckBox = (id: string, phone: any, index: number) => {
    if (!phone.isVerified) {
      dispatch(showToast({
        type: 'info',
        message: 'Please verify the phone number',
      }))
      return
    }
    setModalData({ phone, index })
  }

  const handleSwitchNumbers = () => {
    if (!modalData) return

    const { phone } = modalData
    dispatch(updatePhoneNumber({ id: phone.id, field: 'phone_number', value: phone_number }))
    dispatch(updatePhoneNumber({ id: phone.id, field: 'country_code', value: country_code }))
    dispatch(updatePhoneNumber({ id: phone.id, field: 'isVerified', value: 'true' }))
    dispatch(setVerifiedSecondaryPhoneNumbers({ id: Number(phone.id) - 1, value: '+' + phone_number }))
    // dispatch(setVerifiedPhoneNumber(phone.value))
    dispatch(setUpdateMDStates({ key: 'phone_number', value: phone.value }))
    dispatch(setUpdateMDStates({ key: 'country_code', value: phone.countryCode }))
    setModalData(null)
  }

  const renderPhoneNumberInput = (phone: any, index: number) => (
    <div key={phone.id} className="px-5 w-full sm:w-1/2 md:w-1/3 h-full relative">
      <InputFieldForPhoneNumber
        inputItem={{
          labelName: `${translate('Secondary Phone Number')} (${index + 1}/5)`,
          id: phone.id,
        }}
        errorMessage={phone.error}
        fieldError={phone.duplicateError}
        disabled={disabled}
        required={false}
        onChange={(field, value) => handleChange(phone.id, field, value)}
        value={phone.value}
        isVerified={phone.isVerified}
        ButtonLoading={phone.loading}
        handleVerify={(id, resend) =>
          handleVerify?.(id, resend, "secondary_phone_number", id)
        }
        handleSubmitOTP={() => handleSubmitOTP(phone.id)}
        isOpenOtpModel={isOpenOtpModel}
        setIsOpenOtpModel={setIsOpenOtpModel}
        countryCode={phone?.countryCode}
        editLoading={editLoading}
        mapping={mapping}
        customClass={`border borer-[#72latitude94C7] w-full h-[56px] rounded-[8px]`}
        maxLength={maxLength}
      />
      <div className="absolute right-5 flex items-center gap-2 top-1">
        {index === phoneNumbers.length - 1 && phoneNumbers.length < 5 && (
          <Image
            src="plus"
            testId="add_phone_number"
            className="h-4 w-4 cursor-pointer"
            onClick={handleAddPhoneNumber}
          />
        )}
        {phoneNumbers.length > 1 && (
          <Image
            testId={`delete_phone_number_${index}`}
            src="delete_blue"
            className="h-4 w-4 cursor-pointer"
            onClick={() => handleRemovePhoneNumber(phone.id)}
          />
        )}
      </div>
      {edit && (
        <CheckBoxWithLabel
          label="Set as Primary Number"
          handelCheckBox={(id) => handleCheckBox(id, phone, index)}
          checked={false}
          id={`set-primary-number-${index}`}
        />
      )}
      {edit && index === phoneNumbers.length - 1 && <CustomModal
        isOpen={!!modalData}
        onClose={() => setModalData(null)}
        primaryButtonText="Switch"
        secondaryButtonText="Cancel"
        handelSubmit={handleSwitchNumbers}
        title="Switch Primary Number"
        subText="This action will switch your primary number to secondary."
      />
      }
    </div>
  )

  // return (
  //   // <div className="flex gap-x-14 gap-y-4 flex-wrap">
  //   { phoneNumbers.map((phone: { id: string; error: string; value: string; isVerified: boolean; loading: boolean; countryCode: string }, index:any) => renderPhoneNumberInput(phone, index)) }   
  //     //  {/* <CustomModal
  //     //   isOpen={!!modalData}
  //     //   onClose={() => setModalData(null)}
  //     //   primaryButtonText="Switch"
  //     //   secondaryButtonText="Cancel"
  //     //   handelSubmit={handleSwitchNumbers}
  //     //   title="Switch Primary Number"
  //     //   subText="This action will switch your primary number to secondary."
  //     // /> */}
  //   // </div>
  // );
  return phoneNumbers.map((phone, index) => renderPhoneNumberInput(phone, index));
}
